<template>
    <v-dialog
        v-model="modelValue"
        width="750px"
        max-width="90%"
        :fullscreen="$vuetify.breakpoint.xs"
        persistent
    >
        <v-card v-if="lead" class="q-lead-card-dialog renewed-form p-6" min-height="">
            <v-row wrap>
                <v-col cols="12">
                    <div class="d-flex flex-nowrap">
                        <h3 class="flex-grow-1">{{ lead.FirstName }} {{ lead.LastName }}</h3>
                        <v-btn
                            icon
                            class="ml-2 align-self-start flex-shrink-0"
                            @click="close()"
                        >
                            <v-icon>icon-x-close-q</v-icon>
                        </v-btn>
                    </div>
                </v-col>

                <v-col cols="12">
                    <v-form ref="form" v-model="isValid" lazy-validation>
                        <div class="mb-8 d-flex gap-6 flex-col md:flex-row align-start">
                            <button
                                v-if="lead.PDFURL"
                                class="nowrap underline! text-gray-900! font-bold decoration-2! hover:decoration-2! underline-offset-3 text-base"
                                @click.prevent="openPDF"
                            >
                                <v-icon class="text-gray-900!" style="font-size: 24px !important;">icon-file-text-q</v-icon>
                                <span>Original Lead</span>
                            </button>
                            <button
                                :disabled="!optBtnEnable"
                                class="nowrap underline! text-gray-900! disabled:opacity-30 font-bold decoration-2! hover:decoration-2! underline-offset-3 text-base"
                                @click.prevent="pushLeadToOpt(lead.LeadCode)"
                            >
                                <span>Push Lead to Opt</span>
                                <v-icon class="text-gray-900!" style="font-size: 24px !important;">icon-link-external-q</v-icon>
                            </button>
                            <router-link
                                :to="'/newbusiness/fif-form?lead_code=' + lead.LeadCode"
                                target="_blank"
                                class="nowrap underline! text-gray-900! font-bold decoration-2! hover:decoration-2! underline-offset-3 text-base"
                            >
                                <span>Financial Information Form</span>
                                <v-icon class="text-gray-900!" style="font-size: 24px !important;">icon-link-external-q</v-icon>
                            </router-link>
                        </div>
                        <div class="grid md:grid-cols-2 gap-x-6 mb-4">
                            <div v-if="unknownLeadStatus || creditLeadStatus" class="text-sm">
                                <strong>Status</strong>: {{ lead.LeadStatus }}
                            </div>
                            <QRenewedSelect
                                v-else
                                v-model="leadStatus"
                                :disabled="agentIssue"
                                :items="statusOptions"
                                label="Status"
                                item-text="LeadStatus"
                                item-value="LeadStatus"
                                :messages="leadStatus && 'Currently, some statuses can only be updated in Opt.'"
                                required
                                hide-required-asterisk
                                clearable
                                clear-icon="icon-x-close-q"
                                item-color="q-lead-card-dialog"
                                :menu-props="{ bottom: true, nudgeBottom: 56 }"
                            />
                        </div>
                        <div class="grid grid-cols-2 md:grid-cols-3 gap-6 text-sm leading-none">
                            <strong class="col-span-2 md:col-span-3 text-lg">General Information</strong>
                            <div class="d-flex flex-col gap-3">
                                <strong>Age</strong>
                                <span>{{ lead.Age || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Date of Birth</strong>
                                <span>{{ birthday }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Gender</strong>
                                <span class="text-capitalize">{{ gender || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Tobacco Use</strong>
                                <span class="text-capitalize">{{ tobaccoUse || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <v-icon class="align-self-start text-gray-900!" style="font-size: 24px !important;">icon-q-phone-call</v-icon>
                                <strong>Phone</strong>
                                <a v-if="lead.Phone" :href="'tel:' + lead.Phone" class="nowrap underline! text-brand-500!">
                                    {{ phoneFormatted }}
                                </a>
                                <span v-else>{{ NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <v-icon class="align-self-start text-gray-900!" style="font-size: 24px !important;">icon-q-map-pin</v-icon>
                                <strong>Address</strong>
                                <span v-if="lead.Street" class="underline! text-brand-500 pointer" @click="openGoogleMaps()">{{ lead.Street }}, {{ lead.City }}, {{ lead.State }} {{ lead.Zip }}</span>
                                <span v-else>{{ NA_VALUE }}</span>
                                <span>County: {{ lead.County || NA_VALUE }}</span>
                            </div>
                        </div>
                        <v-divider class="my-8 border-gray-500!" />

                        <div class="grid grid-cols-2 md:grid-cols-3 gap-6 text-sm leading-none">
                            <strong class="col-span-2 md:col-span-3 text-lg">Lead Information</strong>
                            <div class="d-flex flex-col gap-3">
                                <strong>Exclusivity End Date</strong>
                                <span>{{ lead.ExclusivityEndDate || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Lead Code</strong>
                                <span>{{ lead.LeadCode || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Lead Type</strong>
                                <span>{{ lead.LeadType || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Lead Level</strong>
                                <span>{{ lead.LeadLevel || NA_VALUE }}</span>
                            </div>
                            <div v-if="hasRole(['Staff', 'Exec', 'SuperAdmin'])" class="d-flex flex-col gap-3">
                                <strong>Source</strong>
                                <span>{{ lead.LeadSource || NA_VALUE }}</span>
                            </div>
                            <div v-if="hasRole(['Staff', 'Exec', 'SuperAdmin'])" class="d-flex flex-col gap-3">
                                <strong>Subsource</strong>
                                <span>{{ lead.LeadSubSource || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Assign Date</strong>
                                <span>{{ assignDate }}</span>
                            </div>
                        </div>
                        <v-divider class="my-8 border-gray-500!" />

                        <div class="grid grid-cols-2 md:grid-cols-3 gap-6 text-sm leading-none">
                            <strong class="col-span-2 md:col-span-3 text-lg">Mortgage Information</strong>
                            <div class="d-flex flex-col gap-3">
                                <strong>Mortgage Amount</strong>
                                <span class="d-flex align-start"><small v-if="mortgageAmount !== NA_VALUE" class="font-size-10">$</small>{{ mortgageAmount }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Purchase Amount</strong>
                                <span class="d-flex align-start"><small v-if="purchaseAmount !== NA_VALUE" class="font-size-10">$</small>{{ purchaseAmount }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Requested Coverage Amount</strong>
                                <span class="d-flex align-start"><small v-if="requestedCoverageAmount !== NA_VALUE" class="font-size-10">$</small>{{ requestedCoverageAmount }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Loan Amount</strong>
                                <span class="d-flex align-start"><small v-if="loanAmount !== NA_VALUE" class="font-size-10">$</small>{{ loanAmount }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Has Co-Borrower</strong>
                                <span>{{ hasCoBorrower || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Loan Date</strong>
                                <span>{{ loanDate }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Mortgage Date</strong>
                                <span>{{ mortgageDate }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Lender Name</strong>
                                <span>{{ metaData.lender || NA_VALUE }}</span>
                            </div>
                            <div class="d-flex flex-col gap-3">
                                <strong>Caller ID</strong>
                                <span>{{ metaData.caller_id || NA_VALUE }}</span>
                            </div>
                        </div>
                        <v-divider class="my-8 border-gray-500!" />

                        <div class="grid grid-cols-1 gap-x-6 mb-4">
                            <QRenewedTextArea
                                v-model="comments"
                                required
                                hide-required-asterisk
                                label="Comments"
                            />
                        </div>
                        <v-progress-linear v-if="saving" color="brand-500" class="mb-2" indeterminate />
                        <div>
                            <div class="d-flex flex-col md:flex-row justify-end gap-4">
                                <v-btn class="renewed-btn border-2 rounded-[5px]!" color="brand-500" outlined @click="close()">Cancel</v-btn>
                                <v-btn
                                    :disabled="notChanged || saving || agentIssue || !isValid"
                                    class="renewed-btn rounded-[5px]!"
                                    color="brand-500"
                                    dark
                                    @click="save()"
                                >Save</v-btn>
                            </div>
                        </div>
                    </v-form>
                </v-col>
            </v-row>
        </v-card>
    </v-dialog>
</template>

<script>
import QRenewedSelect from '@/components/utils/RenewedForm/QRenewedSelect.vue';
import QRenewedTextArea from '@/components/utils/RenewedForm/QRenewedTextArea.vue';

const NA_VALUE = "Not Available"

export default {
    name: "QLeadCardDialog",
    props: {
        value: {
            type: Boolean,
            required: true,
        },
        lead: {
            type: Object,
            required: false,
        },
        agent: {
            type: Object,
            required: false,
        },
        statusOptions: {
            type: Array,
            required: true,
        },
        optBtnEnable: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            NA_VALUE,
            saving: false,
            leadStatus: null,
            comments: '',
            isValid: true,
        };
    },
    computed: {
        modelValue: {
            get() {
                return this.value && Boolean(this.lead);
            },
            set(value) {
                this.$emit('input', value);
            },
        },
        notChanged() {
            return this.comments === this.lead?.Comments && this.leadStatus === this.lead?.LeadStatus
        },
        metaData() {
            return this.lead.MetaData ?? {}
        },
        birthday() {
            return this.lead.Birthday
                ? this.formatDate(this.lead.Birthday, 'MM/DD/YYYY')
                : NA_VALUE
        },
        gender() {
            return this.lead.Sex ?? this.metaData.Sex ?? this.findInMetaData('gender')
        },
        tobaccoUse() {
            let value = this.metaData["Smoker?"] ?? this.findInMetaData('tobacco') ?? this.findInMetaData('smoke');
            value = this.checkBooleanValue(value)

            return value || NA_VALUE
        },
        assignDate() {
            return this.lead.AssignDate
                ? this.formatDate(this.lead.AssignDate, 'MM/DD/YYYY')
                : NA_VALUE
        },
        purchaseAmount() {
            const purchaseAmount = this.lead.PurchaseAmount ?? this.metaData.purchase_amount
            return typeof purchaseAmount === 'number' || Number(purchaseAmount) >= 0
                ? this.formatLongCurrency(this.lead.PurchaseAmount).substring(1)
                : NA_VALUE
        },
        mortgageDate() {
            const mortgageDate = this.metaData['Mortgage Date']
            return mortgageDate
                ? this.formatDate(mortgageDate, 'MM/DD/YYYY')
                : NA_VALUE
        },
        mortgageAmount() {
            const mortgageAmount = this.metaData.MortgageAmount ?? this.metaData.mortgage_amount
            return typeof mortgageAmount === 'number' || Number(mortgageAmount) >= 0
                ? this.formatLongCurrency(mortgageAmount).substring(1)
                : NA_VALUE
        },
        loanDate() {
            const loanDate = this.metaData['Loan Date']
            return loanDate
                ? this.formatDate(loanDate, 'MM/DD/YYYY')
                : NA_VALUE
        },
        phoneFormatted() {
            return this.formatPhoneNumber(this.lead.Phone)
        },
        requestedCoverageAmount() {
            const amount = this.metaData['Requested Coverage Amount']
            return typeof amount === 'number' || Number(amount) >= 0
                ? this.formatLongCurrency(amount).substring(1)
                : NA_VALUE
        },
        loanAmount() {
            const loanAmount = this.lead.LoanAmount ?? this.metaData['Loan Amount']
            return typeof loanAmount === 'number' || Number(loanAmount) >= 0
                ? this.formatLongCurrency(loanAmount).substring(1)
                : NA_VALUE
        },
        hasCoBorrower() {
            const value = this.findInMetaData('co-borrower')
            return value
        },
        unknownLeadStatus() {
            return this.lead.LeadStatus !== 'New Lead' && !this.statusOptions.some((option) => option.LeadStatus === this.lead.LeadStatus)
        },
        creditLeadStatus() {
            return this.lead.LeadStatus?.startsWith('Credit')
        },
        agentIssue() {
            return this.user.Agent.AgentCode !== this.agent.AgentCode
        },
    },
    methods: {
        findInMetaData(keyWord) {
            if(!this.lead.MetaData) return ""

            const keyWordFormatted = keyWord.toLowerCase()
            const entries = Object.entries(this.metaData)
            const entry = entries
                .find(([key, value]) => key.includes('title') && value.toLowerCase().includes(keyWordFormatted))

            if(!entry) return ""

            const answerKey = entry[0].replace('title', 'answer')
            const value = (entries.find(([key]) => key === answerKey)?.[1] || "").toLowerCase()
            return this.checkBooleanValue(value)
        },
        checkBooleanValue(value) {
            if(typeof value !== 'boolean' && !value) return value

            const formatted = value.toString().toLowerCase()

            if(formatted === "y" || formatted === "true" || formatted === "yes" || formatted === "1") return "Yes"
            if(formatted === "n" || formatted === "false" || formatted === "no" || formatted === "0") return "No"
            return value
        },
        openGoogleMaps() {
            const url = "https://maps.google.com/?q=" + this.lead.Street + ", " + this.lead.City + ", " + this.lead.State + ", " + this.lead.Zip;
            window.open(url, "_blank");
        },
        close() {
            this.modelValue = false;
        },
        async save() {
            this.$refs.form.validate()
            await this.$nextTick()
            if (!this.isValid) return

            this.saving = true
            this.$emit('save', {
                ...this.lead,
                LeadStatus: this.leadStatus,
                Comments: this.comments,
            })
        },
        pushLeadToOpt(leadcode) {
            this.$emit('push-lead-to-opt', leadcode)
        },
        openPDF: function() {
            window.open(this.lead.PDFURL);
        },
    },
    watch: {
        lead(newValue) {
            this.saving = false
            if (!newValue) return

            this.leadStatus = newValue.LeadStatus;
            this.comments = newValue.Comments;
        },
    },
    components: {
        QRenewedSelect,
        QRenewedTextArea,
    },
}
</script>

<style lang="scss">
.v-list-item.q-lead-card-dialog--text.v-list-item--active {
    background-color: var(--color-gray-200);
    color: var(--color-gray-900);
    font-weight: 600;
}

.v-dialog--fullscreen {
    .renewed-form.p-6 {
        padding: 1.5rem !important;
    }
}

.q-lead-card-dialog {
    .v-label {
        font-size: 0.75rem;
        margin-bottom: 0.5rem;
    }

    h3 {
        font-size: 1.5rem;
    }

    .renewed-btn.v-btn.v-size--default {
        height: 3rem;
    }

    .renewed-btn.v-btn.v-btn--disabled {
        color: var(--color-gray-500) !important;
    }
}
</style>
